import AppCard from '@nxsys/components/AppCard';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppIconButton from '@nxsys/components/AppIconButton';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { useGetPayslipHistory } from '@nxsys/hooks/api';
import { useViewPayslipById } from '@nxsys/hooks/api/payroll/usePayrollApi';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { PayslipItem } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const PayslipHistory = () => {
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );

  const subcontractorId = user?.subcontractorId?.id ?? '';
  const paymentModel =
    user?.subcontractorId?.setting?.paymentModel ||
    (user?.subcontractorId as any)?.paymentModel;
  const { value, loading, run } = useGetPayslipHistory();
  const { run: downloadPayslip } = useViewPayslipById();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    if (subcontractorId) {
      run(subcontractorId);
    }
  }, [subcontractorId]);

  const isCIS =
    paymentModel === PAYMENT_MODEL_ENUM.CIS ||
    paymentModel === PAYMENT_MODEL_ENUM.NON_CIS;

  const label = isCIS ? 'Self Bill' : 'Payslip';

  const list = value?.data ?? [];

  const handleDownloadPayslip = async (selfBillRefId: string) => {
    if (!selfBillRefId || downloadingId) return;
    setDownloadingId(selfBillRefId);
    try {
      const result = await downloadPayslip(selfBillRefId);
      if (result?.data) {
        window.open(result.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const columns: ColumnsType<PayslipItem> = [
    {
      title: 'Tax Period',
      key: 'taxPeriod',
      align: 'right',
      width: 120,
      render: (_, record) =>
        `${record?.taxPeriod ?? ''}/${record?.taxYear ?? ''}`,
    },
    {
      title: 'Amount',
      dataIndex: 'netPay',
      key: 'netPay',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
      width: 140,
    },
    {
      title: '',
      key: 'action',
      width: 80,
      align: 'left',
      render: (_, record) => {
        const isDownloading = downloadingId === record.id;
        return (
          <AppIconButton
            title="Download"
            icon={<Icons.IconDownload />}
            loading={isDownloading}
            onClick={() => handleDownloadPayslip(record.id)}
            className="text-neutral-500 hover:text-primary-600"
          />
        );
      },
    },
  ];

  return (
    <AppCard className="h-full">
      <div className="p-[20px]">
        <div className="text-[16px] font-[500] mb-[6px]">{label} History</div>
        <div className="text-[12px] font-[400] text-neutral-500">
          Your 3 most recent {label.toLowerCase()}s.
        </div>
      </div>
      <AppTableContainer
        rowKey="id"
        data={list}
        loading={loading}
        page={1}
        total={list.length}
        pageSize={10}
        pagination={false}
        scroll={{ x: 300 }}
        columns={columns}
      />
    </AppCard>
  );
};

export default PayslipHistory;
